import React, { useRef, useEffect, useState, useCallback } from 'react'
import { evaluateGameStats, subscribeAchievementUnlocked } from './achievements'
import { playOrbSound, playHitSound, playTrophySound } from './audio'

export function NeuroReflejos({ polar, demo, hrv, onExit }) {
  const canvasRef = useRef(null)

  // Estados de React
  const [gameState, setGameState] = useState('menu') // 'menu' | 'playing' | 'gameover'
  const [score, setScore] = useState(0)
  const [combo, setCombo] = useState(0)
  const [maxCombo, setMaxCombo] = useState(0)
  const [hits, setHits] = useState(0)
  const [misses, setMisses] = useState(0)
  const [timeLeft, setTimeLeft] = useState(60)
  const [highScore, setHighScore] = useState(() => {
    try { return parseInt(localStorage.getItem('manabi_reflex_highscore') || '0', 10) } catch { return 0 }
  })
  const [unlockedToast, setUnlockedToast] = useState(null)
  const [lastStats, setLastStats] = useState(null)

  // Referencias mutables para el bucle 60 FPS
  const stateRef = useRef({
    score: 0,
    combo: 0,
    maxCombo: 0,
    hits: 0,
    misses: 0,
    timeLeft: 60,
    targets: [], // { id, x, y, r, maxR, timer, maxTime, isTrap }
    particles: [],
    spawnTimer: 0,
    spawnInterval: 0.75,
    currentHr: 72,
    isZenFocus: true, // BPM <= 80
    startTime: 0,
  })

  // Pulso real o simulado
  const liveHr = (polar && polar.status === 'connected' && polar.hr)
    ? polar.hr
    : (demo && demo.active && demo.hr)
      ? demo.hr
      : 72

  const isBleConnected = (polar && polar.status === 'connected') || (demo && demo.active)

  useEffect(() => {
    stateRef.current.currentHr = liveHr
    stateRef.current.isZenFocus = liveHr <= 80
  }, [liveHr])

  // Suscripción a logros
  useEffect(() => {
    const unsub = subscribeAchievementUnlocked(ach => {
      playTrophySound()
      setUnlockedToast(ach)
      setTimeout(() => setUnlockedToast(null), 3600)
    })
    return unsub
  }, [])

  // Iniciar partida
  const startGame = useCallback(() => {
    stateRef.current = {
      score: 0,
      combo: 0,
      maxCombo: 0,
      hits: 0,
      misses: 0,
      timeLeft: 60,
      targets: [],
      particles: [],
      spawnTimer: 0,
      spawnInterval: 0.75,
      currentHr: liveHr,
      isZenFocus: liveHr <= 80,
      startTime: Date.now(),
    }
    setScore(0)
    setCombo(0)
    setMaxCombo(0)
    setHits(0)
    setMisses(0)
    setTimeLeft(60)
    setGameState('playing')
  }, [liveHr])

  // Clic / toque en el canvas
  const handlePointerDown = (e) => {
    if (gameState !== 'playing') return
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY

    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    const touchX = (clientX - rect.left) * scaleX
    const touchY = (clientY - rect.top) * scaleY

    const s = stateRef.current
    let hitFound = false

    for (let i = s.targets.length - 1; i >= 0; i--) {
      const t = s.targets[i]
      const dx = touchX - t.x
      const dy = touchY - t.y
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (dist <= t.r + 12) {
        hitFound = true
        if (t.isTrap) {
          // Tocó una trampa
          playHitSound()
          s.score = Math.max(0, s.score - 50)
          s.combo = 0
          s.misses += 1
          setCombo(0)
          setMisses(s.misses)
          setScore(s.score)
        } else {
          // Diana acertada
          playOrbSound()
          const mult = s.isZenFocus ? 3 : s.currentHr <= 95 ? 2 : 1
          const pts = (10 + Math.min(20, s.combo * 2)) * mult
          s.score += pts
          s.combo += 1
          if (s.combo > s.maxCombo) s.maxCombo = s.combo
          s.hits += 1
          setScore(s.score)
          setCombo(s.combo)
          setMaxCombo(s.maxCombo)
          setHits(s.hits)

          // Partículas de impacto
          for (let p = 0; p < 10; p++) {
            s.particles.push({
              x: t.x, y: t.y,
              vx: (Math.random() - 0.5) * 220,
              vy: (Math.random() - 0.5) * 220,
              color: s.isZenFocus ? '#64d2ff' : '#30d158',
              life: 0.35
            })
          }
        }
        s.targets.splice(i, 1)
        break
      }
    }

    if (!hitFound) {
      // Clic al aire: corta combo
      s.combo = 0
      setCombo(0)
    }
  }

  // ── Bucle del Juego Canvas a 60 FPS ──
  useEffect(() => {
    if (gameState !== 'playing') return

    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let animId = null
    let lastTime = performance.now()

    const loop = (now) => {
      const dt = Math.min(50, now - lastTime) / 1000
      lastTime = now

      const s = stateRef.current
      const width = canvas.width
      const height = canvas.height

      // Actualizar cronómetro de 60 segundos
      s.timeLeft -= dt
      setTimeLeft(Math.max(0, Math.ceil(s.timeLeft)))

      if (s.timeLeft <= 0) {
        // Fin de la partida
        playTrophySound()
        const durationSec = 60
        const stats = {
          score: s.score,
          hits: s.hits,
          misses: s.misses,
          maxCombo: s.maxCombo,
          accuracy: s.hits + s.misses > 0 ? Math.round((s.hits / (s.hits + s.misses)) * 100) : 0,
          durationSec,
          isConnected: isBleConnected
        }
        setLastStats(stats)
        evaluateGameStats({
          score: s.score,
          durationSec,
          calmSec: s.isZenFocus ? 30 : 0,
          orbs: s.hits,
          hits: s.misses,
          isConnected: isBleConnected,
          maxRmssd: 55
        })

        if (s.score > highScore) {
          setHighScore(s.score)
          try { localStorage.setItem('manabi_reflex_highscore', String(s.score)) } catch (_) {}
        }
        setGameState('gameover')
        return
      }

      // Generación de dianas
      s.spawnTimer += dt
      const maxTargets = s.isZenFocus ? 4 : 5
      if (s.spawnTimer >= s.spawnInterval && s.targets.length < maxTargets) {
        s.spawnTimer = 0
        const pad = 50
        const randX = pad + Math.random() * (width - pad * 2)
        const randY = pad + Math.random() * (height - pad * 2)
        const isTrap = Math.random() < 0.18 // 18% probabilidad de trampa
        const maxTime = s.isZenFocus ? 1.4 : 1.05 // Más tiempo de reacción en calma

        s.targets.push({
          id: Math.random(),
          x: randX,
          y: randY,
          r: 28,
          timer: maxTime,
          maxTime,
          isTrap
        })
      }

      // Actualizar dianas y chequear expiración
      for (let i = s.targets.length - 1; i >= 0; i--) {
        const t = s.targets[i]
        t.timer -= dt

        if (t.timer <= 0) {
          // Se escapó la diana
          if (!t.isTrap) {
            s.misses += 1
            s.combo = 0
            setMisses(s.misses)
            setCombo(0)
            playHitSound()
          }
          s.targets.splice(i, 1)
        }
      }

      // Actualizar partículas
      for (let i = s.particles.length - 1; i >= 0; i--) {
        const p = s.particles[i]
        p.x += p.vx * dt
        p.y += p.vy * dt
        p.life -= dt
        if (p.life <= 0) s.particles.splice(i, 1)
      }

      // ── RENDERIZADO VISUAL ──
      ctx.clearRect(0, 0, width, height)

      // Fondo arena ciberespacial
      const bg = ctx.createRadialGradient(width / 2, height / 2, 20, width / 2, height / 2, width / 1.5)
      bg.addColorStop(0, '#0a1020')
      bg.addColorStop(1, '#05070d')
      ctx.fillStyle = bg
      ctx.fillRect(0, 0, width, height)

      // Cuadrícula sutil de precisión
      ctx.strokeStyle = s.isZenFocus ? 'rgba(100,210,255,0.08)' : 'rgba(255,255,255,0.05)'
      ctx.lineWidth = 1
      for (let x = 40; x < width; x += 60) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke()
      }
      for (let y = 40; y < height; y += 60) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke()
      }

      // Dibujar Dianas Reactivas
      s.targets.forEach(t => {
        const progress = Math.max(0, t.timer / t.maxTime)
        const curR = t.r * (0.8 + 0.2 * progress)

        if (t.isTrap) {
          // Trampa roja (peligro: no tocar)
          ctx.beginPath()
          ctx.arc(t.x, t.y, curR, 0, Math.PI * 2)
          ctx.fillStyle = 'rgba(239,68,68,0.25)'
          ctx.fill()
          ctx.strokeStyle = '#ef4444'
          ctx.lineWidth = 2.5
          ctx.stroke()

          // Cruz de peligro
          ctx.strokeStyle = '#ffffff'
          ctx.lineWidth = 3
          ctx.beginPath()
          ctx.moveTo(t.x - 8, t.y - 8); ctx.lineTo(t.x + 8, t.y + 8)
          ctx.moveTo(t.x + 8, t.y - 8); ctx.lineTo(t.x - 8, t.y + 8)
          ctx.stroke()
        } else {
          // Diana normal (tocar rápido)
          const targetColor = s.isZenFocus ? '#64d2ff' : '#30d158'
          ctx.shadowColor = targetColor
          ctx.shadowBlur = 14

          // Anillo exterior de contracción de tiempo
          ctx.beginPath()
          ctx.arc(t.x, t.y, t.r + 10 * progress, 0, Math.PI * 2)
          ctx.strokeStyle = `${targetColor}80`
          ctx.lineWidth = 2
          ctx.stroke()

          // Cuerpo de la diana
          ctx.beginPath()
          ctx.arc(t.x, t.y, curR, 0, Math.PI * 2)
          ctx.fillStyle = `${targetColor}30`
          ctx.fill()
          ctx.strokeStyle = targetColor
          ctx.lineWidth = 2.5
          ctx.stroke()

          // Centro luminoso
          ctx.beginPath()
          ctx.arc(t.x, t.y, 6, 0, Math.PI * 2)
          ctx.fillStyle = '#ffffff'
          ctx.fill()
          ctx.shadowBlur = 0
        }
      })

      // Dibujar partículas
      s.particles.forEach(p => {
        ctx.beginPath()
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2)
        ctx.fillStyle = p.color
        ctx.fill()
      })

      animId = requestAnimationFrame(loop)
    }

    animId = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(animId)
  }, [gameState, highScore, isBleConnected])

  return (
    <div style={{
      position: 'relative', width: '100%', maxWidth: 840, margin: '0 auto',
      background: '#07090e', borderRadius: 18, border: '1px solid rgba(255,255,255,0.12)',
      overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none'
    }}>
      {/* ── 1. HUD Superior en Vivo ── */}
      <div style={{
        padding: '10px 16px', background: 'rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 10
      }}>
        {/* Pulso y Foco Quirúrgico */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 9, height: 9, borderRadius: '50%',
            background: liveHr <= 80 ? '#64d2ff' : liveHr <= 95 ? '#30d158' : '#ff9f0a',
            boxShadow: `0 0 8px ${liveHr <= 80 ? '#64d2ff' : liveHr <= 95 ? '#30d158' : '#ff9f0a'}`,
          }} />
          <span style={{ fontSize: 13, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {liveHr} <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>BPM</span>
          </span>
          <span style={{
            fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 6,
            background: liveHr <= 80 ? 'rgba(100,210,255,0.25)' : 'rgba(48,209,88,0.2)',
            color: liveHr <= 80 ? '#64d2ff' : '#30d158'
          }}>
            {liveHr <= 80 ? 'FOCO QUIRÚRGICO x3' : liveHr <= 95 ? 'NORMAL x2' : 'ESTRÉS ALTO x1'}
          </span>
        </div>

        {/* Combo y Cronómetro */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {combo > 1 && (
            <span style={{ fontSize: 12, fontWeight: 900, color: '#ffd700', animation: 'pulse 0.4s ease' }}>
              COMBO x{combo}
            </span>
          )}
          <span style={{ fontSize: 13, fontWeight: 800, color: timeLeft <= 10 ? '#ff3b30' : 'rgba(255,255,255,0.6)', fontVariantNumeric: 'tabular-nums' }}>
            ⏱ {timeLeft}s
          </span>
          <span style={{ fontSize: 20, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {score} <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>PTS</span>
          </span>
        </div>

        {/* Salir */}
        <button
          onClick={onExit}
          style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6,
            color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 700,
            padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit'
          }}
        >
          ✕ Salir
        </button>
      </div>

      {/* ── 2. Canvas de las Dianas ── */}
      <div
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        style={{ position: 'relative', width: '100%', height: 420, cursor: 'crosshair' }}
      >
        <canvas
          ref={canvasRef}
          width={840}
          height={420}
          style={{ width: '100%', height: '100%', display: 'block' }}
        />

        {/* Notificación Toast de Trofeo */}
        {unlockedToast && (
          <div style={{
            position: 'absolute', top: 16, right: 16,
            background: 'rgba(15, 20, 32, 0.92)', border: '1.5px solid #ffd700',
            borderRadius: 12, padding: '8px 14px', boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', gap: 10,
            animation: 'fadeIn 0.25s ease', backdropFilter: 'blur(12px)', zIndex: 30
          }}>
            <span style={{ fontSize: 16 }}>★</span>
            <div>
              <span style={{ fontSize: 9.5, fontWeight: 800, color: '#ffd700', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block' }}>
                ¡Trofeo Desbloqueado!
              </span>
              <span style={{ fontSize: 12, fontWeight: 900, color: '#fff' }}>
                {unlockedToast.title}
              </span>
            </div>
          </div>
        )}

        {/* ── Pantalla de Inicio ── */}
        {gameState === 'menu' && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle, rgba(15,20,32,0.95) 0%, rgba(6,8,14,0.98) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', zIndex: 20
          }}>
            <h2 style={{ fontSize: 26, fontWeight: 900, color: '#fff', margin: '0 0 8px', letterSpacing: -0.5 }}>
              Neuro-Reflejos bajo Presión
            </h2>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', maxWidth: 460, margin: '0 0 16px', lineHeight: 1.5 }}>
              Tocá las dianas luminosas lo más rápido posible antes de que se encojan.
              <br />
              <strong style={{ color: '#ef4444' }}>¡Cuidado con las rojas con cruz!</strong> Son trampas que restan puntos y cortan el combo.
              <br />
              <strong style={{ color: '#64d2ff' }}>El desafío de biofeedback:</strong> Si mantenés tu pulso en calma (&le;80 BPM), tus puntos se multiplican x3 y las dianas duran más tiempo.
            </p>
            <button
              onClick={startGame}
              style={{
                background: '#007aff', color: '#fff', border: 'none',
                borderRadius: 12, padding: '12px 30px', fontSize: 14, fontWeight: 900,
                cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 18px rgba(0,122,255,0.4)'
              }}
            >
              ¡INICIAR TEST DE REFLEJOS!
            </button>
          </div>
        )}

        {/* ── Game Over / Resultados ── */}
        {gameState === 'gameover' && lastStats && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle, rgba(15,20,32,0.95) 0%, rgba(6,8,14,0.98) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', zIndex: 20
          }}>
            <h2 style={{ fontSize: 26, fontWeight: 900, color: '#fff', margin: '0 0 10px' }}>
              Test de Reflejos Completado
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, width: '100%', maxWidth: 440, marginBottom: 18 }}>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Puntos</span>
                <span style={{ fontSize: 17, fontWeight: 900, color: '#ffd700' }}>{lastStats.score}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Aciertos</span>
                <span style={{ fontSize: 17, fontWeight: 900, color: '#30d158' }}>{lastStats.hits}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Precisión</span>
                <span style={{ fontSize: 17, fontWeight: 900, color: '#64d2ff' }}>{lastStats.accuracy}%</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Max Combo</span>
                <span style={{ fontSize: 17, fontWeight: 900, color: '#ff9f0a' }}>x{lastStats.maxCombo}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={startGame}
                style={{
                  background: '#007aff', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '10px 22px', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Jugar de Nuevo
              </button>
              <button
                onClick={() => setGameState('menu')}
                style={{
                  background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Menú
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Instrucción al pie ── */}
      <div style={{
        padding: '8px 16px', background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'rgba(255,255,255,0.5)'
      }}>
        <span>Controles: Clic con el mouse o toque en pantalla sobre las dianas</span>
        <span>Biofeedback: Control de estrés y pulso &le; 80 BPM para multiplicador x3</span>
      </div>
    </div>
  )
}
